import styled from "styled-components";
import { keyframes } from "styled-components";

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const Container = styled.div`
  padding: 3rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 85vh;
`;

export const PromoBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
  padding: 1.25rem 2rem;
  margin-bottom: 2.5rem;
  border-radius: 16px;
  background: linear-gradient(135deg, #58a6ff 0%, #79c0ff 100%);
  box-shadow: 0 8px 25px rgba(88, 166, 255, 0.3);
  text-align: center;
`;

export const PromoText = styled.p`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;

  strong {
    font-weight: 800;
  }
`;

export const PromoCodeBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  border: 1.5px dashed rgba(255, 255, 255, 0.8);
  color: #fff;
  font-weight: 800;
  font-size: 1rem;
  letter-spacing: 0.05em;
  padding: 0.5rem 1.1rem;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

export const Header = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  color: var(--text-main);
  margin-bottom: 0.5rem;
  letter-spacing: -0.02em;
`;

export const Description = styled.p`
  font-size: 1.1rem;
  text-align: center;
  color: var(--text-secondary);
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  justify-items: center;
`;

export const TemplateCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 320px;
  background: var(--bg-card);
  border: 2px solid ${props => props.isSelected ? 'var(--primary-color)' : 'var(--border-color)'};
  border-radius: 20px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-8px);
    border-color: ${props => props.isSelected ? 'var(--primary-color)' : 'var(--text-secondary)'};
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
  }

  ${props => props.isSelected && `
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
  `}
`;

export const Badge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => props.purchased ? '#10b981' : 'var(--primary-color)'};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 99px;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 10;
`;

export const PreviewImage = styled.div`
  width: 100%;
  height: 200px;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-secondary);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.02) 100%);
  }
`;

export const TemplateContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const TemplateName = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-main);
  margin-bottom: 0.5rem;
`;

export const TemplateDesc = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.5;
  margin-bottom: 1.5rem;
  flex: 1;
`;

export const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
`;

export const Price = styled.span`
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--text-main);
`;

export const ActionButton = styled.button`
  background: ${props => props.primary ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.primary ? 'white' : 'var(--text-main)'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'var(--border-color)'};
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.primary ? 'var(--primary-hover)' : 'var(--bg-secondary)'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.85;
  }
`;

export const ButtonSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-left: 6px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: ${spin} 0.7s linear infinite;
  vertical-align: middle;
`;

export const LockOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: 5;
`;

